/**
 * Print the topic or article currently on screen.
 * Clones the rendered document so flow diagrams, images, and rich text
 * match the editor and read views, without the app chrome.
 */

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function isVisuallyEmpty(el) {
  if (!el) return true;
  if (el.querySelector('img, svg, table, video, canvas, iframe')) return false;
  const text = String(el.textContent || '').replace(/\u00a0/g, ' ').trim();
  return !text;
}

function unwrap(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function preparePrintClone(clone) {
  clone.querySelectorAll('script, iframe, object, embed').forEach((el) => el.remove());
  clone.querySelectorAll(
    '.guides-title-level-menu, .guides-anno-gutter-layer, .rte-code-gutter, .rte-table-chrome, .rte-media-chrome, .rte-flow-edit'
  ).forEach((el) => el.remove());
  clone.querySelectorAll('.rte-table-sel, .rte-table-sel-preview').forEach((el) => {
    el.classList.remove('rte-table-sel', 'rte-table-sel-preview');
  });
  clone.querySelectorAll('mark.guides-find-hit').forEach(unwrap);
  clone.querySelectorAll('[contenteditable]').forEach((el) => {
    el.removeAttribute('contenteditable');
  });
  clone.querySelectorAll('[draggable]').forEach((el) => {
    el.removeAttribute('draggable');
  });
  clone.querySelectorAll('[data-guides-placeholder]').forEach((el) => {
    if (isVisuallyEmpty(el)) el.remove();
  });
  clone.querySelectorAll('.guides-title-editor').forEach((el) => {
    if (isVisuallyEmpty(el)) el.remove();
  });
}

function collectStyles() {
  const parts = [];
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((node) => {
    if (node.tagName === 'LINK') {
      if (node.href) parts.push(`<link rel="stylesheet" href="${escapeHtml(node.href)}">`);
    } else if (node.textContent) {
      parts.push(node.outerHTML);
    }
  });
  return parts.join('\n');
}

const PRINT_CSS = `
  @page { margin: 0.7in; }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    color: #252F4A !important;
    height: auto !important;
    overflow: visible !important;
  }
  .rte-document-page,
  .guides-read-article {
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }
  .guides-title-level-menu,
  .guides-anno-gutter-layer,
  .rte-code-gutter,
  .rte-table-chrome,
  .rte-media-chrome,
  .rte-flow-edit,
  .rte-caption-catalog {
    display: none !important;
  }
  [data-guides-placeholder]:empty::before,
  [data-guides-placeholder]:has(> br:only-child)::before,
  [data-guides-placeholder]:has(> br:first-child:last-child)::before {
    content: none !important;
  }
  img, svg, table, figure, .rte-flow, pre {
    max-width: 100% !important;
  }
  figure, table, pre, blockquote, .rte-flow, .rte-textbox {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  h1, h2, h3, h4, h5, h6 {
    break-after: avoid;
    page-break-after: avoid;
  }
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`;

function imageElements(doc) {
  const list = doc && doc.images;
  const images = [];
  if (!list || typeof list.length !== 'number') return images;
  // Index access: spreading a collection from the print window throws
  // "Result of the Symbol.iterator method is not an object".
  for (let i = 0; i < list.length; i += 1) images.push(list[i]);
  return images;
}

function whenReady(printWindow) {
  const doc = printWindow.document;
  const fontsReady = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
  const images = imageElements(doc).map((img) => {
    if (!img || img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  });
  const ready = Promise.all([Promise.resolve(fontsReady), ...images]);
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, 2000);
    ready.then(() => {
      clearTimeout(timer);
      resolve();
    }, () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

export function printGuideDocument({ title = 'Document' } = {}) {
  const page = document.querySelector('.guides-topic-editor-col .rte-document-page');
  if (!page) {
    window.alert('Nothing to print on this page.');
    return;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    window.alert('Please allow popups to print this document.');
    return;
  }

  const clone = page.cloneNode(true);
  preparePrintClone(clone);
  const safeTitle = escapeHtml(title || 'Document') || 'Document';

  printWindow.document.open();
  printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <title>${safeTitle}</title>
    <base href="${escapeHtml(document.baseURI)}">
    ${collectStyles()}
    <style>${PRINT_CSS}</style>
  </head>
  <body>${clone.outerHTML}</body>
</html>`);
  printWindow.document.close();

  let started = false;
  const finish = () => {
    try {
      printWindow.focus();
      printWindow.print();
    } catch (_) {
      /* print dialog unavailable */
    }
  };
  const run = () => {
    if (started) return;
    started = true;
    try {
      whenReady(printWindow).then(finish, finish);
    } catch (_) {
      finish();
    }
  };
  printWindow.addEventListener('load', run, { once: true });
  setTimeout(run, 1500);
}
